import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError, formatPrice, sizeStockStatus } from "@/lib/api";
import { useCart } from "@/lib/cart";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { IMG_FALLBACK } from "@/components/Collections";
import useLenis from "@/hooks/useLenis";

const onImgError = (e) => {
  if (e.currentTarget.src !== IMG_FALLBACK) e.currentTarget.src = IMG_FALLBACK;
};

const COLLECTION_LABEL = {
  faucet: "Faucet Collective",
  originals: "GG Originals",
  skate: "Skate",
  goods: "Goods",
};

export default function Product() {
  useLenis();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [wlEmail, setWlEmail] = useState("");
  const [wlDone, setWlDone] = useState(false);
  const [wlLoading, setWlLoading] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        setSize(data.sizes?.[0] || "");
      })
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const comingSoon = product?.status !== "live";
  const sizeStock = sizeStockStatus(product, size);

  const addToBag = () => {
    if (!size) return toast.error("Pick a size first.");
    if (!comingSoon && !sizeStock.purchasable) return toast.error("That size is sold out.");
    addItem(product, size);
    toast.success("Added to your bag.");
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!wlEmail) return;
    setWlLoading(true);
    try {
      const { data } = await api.post(`/products/${slug}/waitlist`, { email: wlEmail, origin_url: window.location.origin });
      setWlDone(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setWlLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#090909] text-[#F2EFEB] flex flex-col items-center justify-center gap-6 font-mono-ui">
        <h1 className="font-display text-7xl">404 / LOST</h1>
        <Link to="/" className="overline border-2 border-[#F2EFEB] px-6 py-3 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">
          ← Back to Good Grief
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F2EFEB] flex items-center justify-center font-mono-ui animate-pulse">
        Loading...
      </div>
    );
  }

  return (
    <main data-testid="product-page" className="w-full overflow-hidden bg-[#F2EFEB] text-[#090909]">
      <Nav />
      <div className="pt-16" />

      <div className="px-4 md:px-8 py-6 border-b-2 border-[#090909] font-mono-ui text-xs uppercase tracking-widest flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate("/")} className="hover:text-[#FF2A00]" data-testid="breadcrumb-home">Home</button>
          <span>/</span>
          <Link to={`/shop/${product.collection}`} className="text-[#FF2A00] hover:underline underline-offset-4" data-testid="breadcrumb-collection">
            {COLLECTION_LABEL[product.collection]}
          </Link>
          <span>/</span>
          <span className="opacity-60 truncate">{product.name}</span>
        </div>
        <button
          onClick={() => navigate(`/shop/${product.collection}`)}
          data-testid="product-back-btn"
          className="shrink-0 border-2 border-[#090909] px-3 py-1.5 hover:bg-[#090909] hover:text-[#F2EFEB] transition-colors"
        >
          ← Back to {COLLECTION_LABEL[product.collection]}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Gallery */}
        <div className="border-b-2 lg:border-b-0 lg:border-r-2 border-[#090909] bg-[#090909] lg:self-start lg:sticky lg:top-20">
          <div className="relative overflow-hidden">
            <motion.img
              key={activeImg}
              initial={{ opacity: 0.4, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              src={product.images?.[activeImg] || IMG_FALLBACK}
              alt={product.name}
              onError={onImgError}
              className="block w-full aspect-square object-cover grayscale contrast-125"
              data-testid="product-main-image"
            />
            {comingSoon && (
              <span className="absolute top-4 left-4 font-mono-ui text-xs uppercase px-3 py-1 border-2 border-[#090909] bg-[#DFFF00] text-[#090909] rotate-[-4deg]">
                Drop 01 — Coming Soon
              </span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 p-3 bg-[#090909]">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 border-2 overflow-hidden ${i === activeImg ? "border-[#DFFF00]" : "border-[#F2EFEB]/40"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover grayscale" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="px-4 md:px-10 py-10 md:py-16">
          <Link to={`/shop/${product.collection}`} className="overline text-[#FF2A00] hover:underline underline-offset-4" data-testid="detail-collection-link">{COLLECTION_LABEL[product.collection]}</Link>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.85] tracking-tight mt-3 break-words">
            {product.name}
          </h1>
          <p className="font-mono-ui text-2xl mt-4" data-testid="product-price">${formatPrice(product.price)}</p>
          <p className="font-serif-ed italic text-lg md:text-xl mt-6 max-w-lg leading-relaxed">
            {product.description}
          </p>

          {/* Sizes */}
          <div className="mt-10">
            <p className="overline mb-3">Select Size</p>
            <div className="flex flex-wrap gap-2" data-testid="product-sizes">
              {product.sizes.map((s) => {
                const soldOut = !comingSoon && !sizeStockStatus(product, s).purchasable;
                return (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    disabled={soldOut}
                    data-testid={`size-${s}`}
                    className={`relative min-w-[3rem] h-12 px-3 border-2 border-[#090909] font-mono-ui text-sm transition-colors ${
                      size === s ? "bg-[#090909] text-[#F2EFEB]" : "bg-transparent hover:bg-[#DFFF00]"
                    } ${soldOut ? "opacity-40 line-through cursor-not-allowed hover:bg-transparent" : ""}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-10 flex flex-col gap-3">
            {!comingSoon && size && sizeStock.label && (
              <span
                data-testid="product-stock-status"
                className={`inline-block w-fit font-mono-ui text-xs uppercase tracking-widest px-3 py-1.5 border-2 ${
                  sizeStock.tone === "out"
                    ? "border-[#FF2A00] text-[#FF2A00]"
                    : "border-[#090909] bg-[#DFFF00] text-[#090909]"
                }`}
              >
                {sizeStock.tone === "warn" ? "Backorder — ships when restocked" : sizeStock.label}
              </span>
            )}
            <button
              onClick={addToBag}
              disabled={!comingSoon && !sizeStock.purchasable}
              data-testid="product-add-to-bag"
              className="h-14 border-2 border-[#090909] bg-[#090909] text-[#F2EFEB] font-mono-ui uppercase text-sm tracking-widest hover:bg-[#FF2A00] hover:border-[#FF2A00] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#090909] disabled:hover:border-[#090909]"
            >
              {!comingSoon && !sizeStock.purchasable ? "Sold Out" : sizeStock.tone === "warn" ? "Backorder →" : "Add to Bag →"}
            </button>
            {comingSoon && (
              <p className="font-mono-ui text-xs opacity-60">
                * You can stack your bag now. Checkout unlocks when Drop 01 lands.
              </p>
            )}
          </div>

          {/* Waitlist */}
          {comingSoon && (
            <div className="mt-10 border-2 border-[#090909] p-5" data-testid="product-waitlist">
              <p className="overline text-[#FF2A00] mb-2">Get notified</p>
              <p className="font-mono-ui text-sm mb-4">
                {product.waitlist_count > 0 && (
                  <span className="text-[#FF2A00]">{product.waitlist_count} already waiting. </span>
                )}
                Be first to know the second this drops.
              </p>
              {wlDone ? (
                <p className="font-mono-ui text-sm bg-[#DFFF00] p-3" data-testid="waitlist-success">
                  ✓ You’re on the waitlist for this one.
                </p>
              ) : (
                <form onSubmit={joinWaitlist} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    required
                    value={wlEmail}
                    onChange={(e) => setWlEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="waitlist-email-input"
                    className="flex-1 h-12 bg-transparent border-b-2 border-[#090909] px-1 font-mono-ui focus:outline-none focus:border-[#FF2A00]"
                  />
                  <button
                    type="submit"
                    disabled={wlLoading}
                    data-testid="waitlist-submit-btn"
                    className="h-12 px-6 border-2 border-[#090909] font-mono-ui uppercase text-xs tracking-widest hover:bg-[#DFFF00] transition-colors disabled:opacity-60"
                  >
                    {wlLoading ? "..." : "Notify Me"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}
